import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, FileDown, Send, Save, PlayCircle } from 'lucide-react';

import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import RisultatoRegola from '../components/sindacato/RisultatoRegola';
import { getCcnlDisponibili } from '../services/sindacatoApi';
import {
  getLeadDetail, aggiungiNota, cambiaStato, eseguiAnalisiCcnl, scaricaReportPdf,
} from '../services/operatoreApi';

const STATI = [
  'nuovo', 'in verifica', 'confermato', 'contattato', 'iscritto', 'vertenza aperta', 'chiuso senza esito',
];

const CEDOLINO_CCNL_INIZIALE = {
  livello: '', anzianita_anni: '', paga_base_rilevata: '',
  trasferta_giorni: '', trasferta_importo_busta: '',
  straordinario_ore: '', straordinario_importo: '', straordinario_tipo: 'diurno',
  giorni_malattia: '', importo_malattia_rilevato: '',
  giorni_infortunio: '', importo_infortunio_rilevato: '',
  giorni_congedo: '', tipo_congedo: 'obbligatoria', importo_congedo_rilevato: '',
};

function numeroOUndefined(v) {
  if (v === '' || v === null || v === undefined) return undefined;
  const n = parseFloat(v);
  return Number.isNaN(n) ? undefined : n;
}

export default function OperatoreLeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dettaglio, setDettaglio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');

  const [nuovoStato, setNuovoStato] = useState('');
  const [salvandoStato, setSalvandoStato] = useState(false);

  const [testoNota, setTestoNota] = useState('');
  const [inviandoNota, setInviandoNota] = useState(false);

  const [ccnlList, setCcnlList] = useState([]);
  const [ccnlSelezionato, setCcnlSelezionato] = useState('');
  const [cedolinoCcnl, setCedolinoCcnl] = useState(CEDOLINO_CCNL_INIZIALE);
  const [eseguendoAnalisi, setEseguendoAnalisi] = useState(false);

  const carica = useCallback(async () => {
    setLoading(true);
    setErrore('');
    try {
      const risposta = await getLeadDetail(id);
      setDettaglio(risposta);
      setNuovoStato(risposta.lead.stato);
      if (risposta.ccnl_suggeriti?.length) {
        // Solo se l'operatore non ha gia' scelto un CCNL: non sovrascrive
        // una selezione manuale ai ricaricamenti successivi (dopo una
        // nota, un cambio stato, ecc.).
        setCcnlSelezionato((prev) => prev || risposta.ccnl_suggeriti[0]);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setErrore('Accesso riservato ai consulenti.');
      } else if (err.response?.status === 404) {
        setErrore('Caso non trovato.');
      } else {
        setErrore('Errore nel caricamento del caso.');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { carica(); }, [carica]);

  useEffect(() => {
    getCcnlDisponibili().then(setCcnlList).catch(() => setCcnlList([]));
  }, []);

  const handleCambiaStato = async () => {
    setSalvandoStato(true);
    try {
      await cambiaStato(id, nuovoStato);
      toast.success('Stato aggiornato');
      carica();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel cambio di stato');
    } finally {
      setSalvandoStato(false);
    }
  };

  const handleAggiungiNota = async () => {
    if (!testoNota.trim()) return;
    setInviandoNota(true);
    try {
      await aggiungiNota(id, testoNota.trim());
      setTestoNota('');
      carica();
    } catch (err) {
      toast.error('Errore nell\'aggiunta della nota');
    } finally {
      setInviandoNota(false);
    }
  };

  const handleEseguiAnalisi = async () => {
    if (!ccnlSelezionato) {
      toast.error('Seleziona un CCNL');
      return;
    }
    setEseguendoAnalisi(true);
    try {
      const payload = {
        livello: cedolinoCcnl.livello || undefined,
        anzianita_anni: numeroOUndefined(cedolinoCcnl.anzianita_anni),
        paga_base_rilevata: numeroOUndefined(cedolinoCcnl.paga_base_rilevata),
        trasferta_giorni: numeroOUndefined(cedolinoCcnl.trasferta_giorni),
        trasferta_importo_busta: numeroOUndefined(cedolinoCcnl.trasferta_importo_busta),
        straordinario_ore: numeroOUndefined(cedolinoCcnl.straordinario_ore),
        straordinario_importo: numeroOUndefined(cedolinoCcnl.straordinario_importo),
        straordinario_tipo: cedolinoCcnl.straordinario_tipo || undefined,
        giorni_malattia: numeroOUndefined(cedolinoCcnl.giorni_malattia),
        importo_malattia_rilevato: numeroOUndefined(cedolinoCcnl.importo_malattia_rilevato),
        giorni_infortunio: numeroOUndefined(cedolinoCcnl.giorni_infortunio),
        importo_infortunio_rilevato: numeroOUndefined(cedolinoCcnl.importo_infortunio_rilevato),
        giorni_congedo: numeroOUndefined(cedolinoCcnl.giorni_congedo),
        tipo_congedo: cedolinoCcnl.tipo_congedo || undefined,
        importo_congedo_rilevato: numeroOUndefined(cedolinoCcnl.importo_congedo_rilevato),
      };
      await eseguiAnalisiCcnl(id, ccnlSelezionato, payload);
      toast.success('Analisi CCNL eseguita');
      carica();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'analisi CCNL');
    } finally {
      setEseguendoAnalisi(false);
    }
  };

  const handleScaricaPdf = async () => {
    try {
      await scaricaReportPdf(id);
      toast.success('Report scaricato');
    } catch (err) {
      toast.error('Errore nel download del report');
    }
  };

  if (loading) {
    return <DashboardLayout><div className="max-w-4xl mx-auto p-8 text-center text-muted-foreground">Caricamento...</div></DashboardLayout>;
  }

  if (errore || !dettaglio) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto space-y-4">
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm">{errore}</div>
          <Button variant="outline" onClick={() => navigate('/operatore')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Torna alla coda
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const { lead, cedolino, ccnl_suggeriti: ccnlSuggeriti } = dettaglio;
  const datiCedolino = cedolino?.dati_cedolino || {};

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="operatore-lead-detail-page">
        <div className="flex items-center justify-between">
          <Link to="/operatore" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-forest">
            <ArrowLeft className="w-4 h-4" /> Torna alla coda
          </Link>
          <Button variant="outline" onClick={handleScaricaPdf} data-testid="download-report-btn">
            <FileDown className="w-4 h-4 mr-2" /> Scarica report PDF
          </Button>
        </div>

        <Card className="swiss-card">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-xl">Caso {lead.id.slice(0, 8)}</CardTitle>
              <Badge variant="outline">Priorità {lead.punteggio_priorita}</Badge>
            </div>
            <CardDescription>
              Settore: <span className="capitalize font-medium">{lead.settore_dichiarato}</span> · Contratto: {lead.tipo_contratto}
              {' '}· Creato il {new Date(lead.creato_il).toLocaleString('it-IT')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {lead.contatto_revocato ? (
              <p className="text-sm text-slate-500 italic">Il lavoratore ha revocato il consenso al contatto: dati di contatto cancellati.</p>
            ) : (
              <p className="text-sm"><strong>{lead.nome}</strong> — {lead.recapito}</p>
            )}
          </CardContent>
        </Card>

        <Card className="swiss-card">
          <CardHeader><CardTitle className="text-lg">Stato del caso</CardTitle></CardHeader>
          <CardContent className="flex items-center gap-3">
            <Select value={nuovoStato} onValueChange={setNuovoStato}>
              <SelectTrigger className="swiss-input w-64" data-testid="select-stato">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATI.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={handleCambiaStato} disabled={salvandoStato || nuovoStato === lead.stato} data-testid="salva-stato-btn">
              <Save className="w-4 h-4 mr-2" /> Salva
            </Button>
          </CardContent>
        </Card>

        <Card className="swiss-card">
          <CardHeader><CardTitle className="text-lg">Cedolino dichiarato</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              {Object.entries(datiCedolino).filter(([, v]) => v !== null && v !== undefined).map(([k, v]) => (
                <div key={k} className="bg-slate-50 p-2 rounded">
                  <p className="text-muted-foreground text-xs">{k}</p>
                  <p className="font-mono font-medium">{String(v)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="swiss-card">
          <CardHeader><CardTitle className="text-lg">Esito del controllo generico</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {lead.esito_generico.risultati.map((r, i) => <RisultatoRegola key={i} risultato={r} />)}
          </CardContent>
        </Card>

        <Card className="swiss-card border-forest/20">
          <CardHeader>
            <CardTitle className="text-lg">Analisi CCNL puntuale</CardTitle>
            <CardDescription>
              CCNL suggerito dal settore: {ccnlSuggeriti?.join(', ') || 'nessuno'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="label-uppercase">CCNL da applicare</Label>
              <Select value={ccnlSelezionato} onValueChange={setCcnlSelezionato}>
                <SelectTrigger className="swiss-input" data-testid="select-ccnl-analisi">
                  <SelectValue placeholder="Seleziona CCNL" />
                </SelectTrigger>
                <SelectContent>
                  {ccnlList.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="label-uppercase text-xs">Livello</Label>
                <Input className="swiss-input" value={cedolinoCcnl.livello}
                  onChange={(e) => setCedolinoCcnl((p) => ({ ...p, livello: e.target.value }))} data-testid="input-livello" />
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase text-xs">Anzianità (anni)</Label>
                <Input type="number" className="swiss-input" value={cedolinoCcnl.anzianita_anni}
                  onChange={(e) => setCedolinoCcnl((p) => ({ ...p, anzianita_anni: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase text-xs">Paga base rilevata</Label>
                <Input type="number" step="0.01" className="swiss-input" value={cedolinoCcnl.paga_base_rilevata}
                  onChange={(e) => setCedolinoCcnl((p) => ({ ...p, paga_base_rilevata: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase text-xs">Giorni trasferta</Label>
                <Input type="number" className="swiss-input" value={cedolinoCcnl.trasferta_giorni}
                  onChange={(e) => setCedolinoCcnl((p) => ({ ...p, trasferta_giorni: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase text-xs">Importo trasferta in busta</Label>
                <Input type="number" step="0.01" className="swiss-input" value={cedolinoCcnl.trasferta_importo_busta}
                  onChange={(e) => setCedolinoCcnl((p) => ({ ...p, trasferta_importo_busta: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase text-xs">Ore straordinario</Label>
                <Input type="number" className="swiss-input" value={cedolinoCcnl.straordinario_ore}
                  onChange={(e) => setCedolinoCcnl((p) => ({ ...p, straordinario_ore: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase text-xs">Importo straordinario</Label>
                <Input type="number" step="0.01" className="swiss-input" value={cedolinoCcnl.straordinario_importo}
                  onChange={(e) => setCedolinoCcnl((p) => ({ ...p, straordinario_importo: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase text-xs">Tipo straordinario</Label>
                <Select value={cedolinoCcnl.straordinario_tipo} onValueChange={(v) => setCedolinoCcnl((p) => ({ ...p, straordinario_tipo: v }))}>
                  <SelectTrigger className="swiss-input"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="diurno">Diurno</SelectItem>
                    <SelectItem value="notturno">Notturno</SelectItem>
                    <SelectItem value="festivo">Festivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />
            <p className="text-sm font-medium text-muted-foreground">Malattia, infortunio, congedi/maternità</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="label-uppercase text-xs">Giorni malattia</Label>
                <Input type="number" className="swiss-input" value={cedolinoCcnl.giorni_malattia}
                  onChange={(e) => setCedolinoCcnl((p) => ({ ...p, giorni_malattia: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase text-xs">Importo malattia rilevato</Label>
                <Input type="number" step="0.01" className="swiss-input" value={cedolinoCcnl.importo_malattia_rilevato}
                  onChange={(e) => setCedolinoCcnl((p) => ({ ...p, importo_malattia_rilevato: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase text-xs">Giorni infortunio</Label>
                <Input type="number" className="swiss-input" value={cedolinoCcnl.giorni_infortunio}
                  onChange={(e) => setCedolinoCcnl((p) => ({ ...p, giorni_infortunio: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase text-xs">Importo infortunio rilevato</Label>
                <Input type="number" step="0.01" className="swiss-input" value={cedolinoCcnl.importo_infortunio_rilevato}
                  onChange={(e) => setCedolinoCcnl((p) => ({ ...p, importo_infortunio_rilevato: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase text-xs">Giorni congedo/maternità</Label>
                <Input type="number" className="swiss-input" value={cedolinoCcnl.giorni_congedo}
                  onChange={(e) => setCedolinoCcnl((p) => ({ ...p, giorni_congedo: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase text-xs">Tipo congedo</Label>
                <Select value={cedolinoCcnl.tipo_congedo} onValueChange={(v) => setCedolinoCcnl((p) => ({ ...p, tipo_congedo: v }))}>
                  <SelectTrigger className="swiss-input"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="obbligatoria">Maternità obbligatoria</SelectItem>
                    <SelectItem value="facoltativa">Congedo facoltativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase text-xs">Importo congedo rilevato</Label>
                <Input type="number" step="0.01" className="swiss-input" value={cedolinoCcnl.importo_congedo_rilevato}
                  onChange={(e) => setCedolinoCcnl((p) => ({ ...p, importo_congedo_rilevato: e.target.value }))} />
              </div>
            </div>

            <Button onClick={handleEseguiAnalisi} disabled={eseguendoAnalisi} data-testid="esegui-analisi-ccnl-btn">
              <PlayCircle className="w-4 h-4 mr-2" /> Esegui analisi CCNL
            </Button>

            {lead.analisi_ccnl_specifiche?.length > 0 && (
              <>
                <Separator />
                <p className="text-sm font-medium text-muted-foreground">Analisi già eseguite</p>
                {lead.analisi_ccnl_specifiche.map((analisi, idx) => (
                  <div key={idx} className="space-y-2 border border-slate-200 rounded-sm p-3">
                    <p className="text-sm font-medium">
                      {analisi.ccnl_nome} — {new Date(analisi.eseguita_il).toLocaleString('it-IT')} — esito complessivo: {analisi.esito_complessivo}
                    </p>
                    {analisi.risultati.map((r, i) => <RisultatoRegola key={i} risultato={r} />)}
                  </div>
                ))}
              </>
            )}
          </CardContent>
        </Card>

        <Card className="swiss-card">
          <CardHeader><CardTitle className="text-lg">Note interne</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {lead.note_interne?.length > 0 ? (
              <div className="space-y-2">
                {lead.note_interne.map((nota) => (
                  <div key={nota.id} className="text-sm bg-slate-50 p-3 rounded-sm">
                    <p className="text-xs text-muted-foreground mb-1">
                      {nota.autore} · {new Date(nota.timestamp).toLocaleString('it-IT')}
                    </p>
                    {nota.testo}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Nessuna nota ancora.</p>
            )}
            <div className="flex gap-3">
              <Textarea
                value={testoNota}
                onChange={(e) => setTestoNota(e.target.value)}
                placeholder="Aggiungi una nota interna..."
                data-testid="input-nota"
              />
              <Button onClick={handleAggiungiNota} disabled={inviandoNota || !testoNota.trim()} data-testid="aggiungi-nota-btn">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
